import {HttpClient} from '@angular/common/http';
import { Injectable, signal, Signal, WritableSignal, inject } from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {EventMessageType} from '../models/event-message-type.enum';
import {MessageHandlerService} from './messageHandler.service';
import {SseEventsService} from './sse-events.service';
import {environment} from '../../../environments/environment';
import {EasytrackNotification, EasytrackNotificationType, ToastService} from 'bs-components';
import {ADMIN_APP_ID} from '../models/admin.constants';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageHandlerService);
  private readonly translate = inject(TranslateService);
  private readonly sseService = inject(SseEventsService);
  private readonly toastService = inject(ToastService);

  private readonly _notifications$: WritableSignal<EasytrackNotification[]> = signal([]);

  private readonly serviceUrl: string = `${environment.baseUrl}/notifications`;

  private readonly audio: HTMLAudioElement = new Audio('assets/audios/notification.mp3');

  constructor() {
    this.sseService
      .onSubject(EventMessageType.NEW_NOTIFICATION)
      .subscribe((event) => {
        this.addNotification(event.data);
        this.playNotificationAudio();
      });
  }

  public getNotifications(): Observable<EasytrackNotification[]> {
    return this.http.get<EasytrackNotification[]>(this.serviceUrl).pipe(
      tap((notifications) => {
        this._notifications$.set(notifications);
      }),
      catchError(
        this.messageService.handleError<EasytrackNotification[]>(
          this.translate.instant('LOGS.NOTIFICATIONS.GET_ALL_FAILURE'),
          []
        )
      )
    );
  }

  public readIfNot(notification: EasytrackNotification): Observable<EasytrackNotification> {
    if (notification.read) {
      return of(notification);
    }
    return this.http.put<EasytrackNotification>(`${this.serviceUrl}/${notification.id}/read`, {})
      .pipe(
        tap(() => {
          this._notifications$.update((notifications) => {
            return notifications.map(n => n.id === notification.id ? this.setNotificationAsRead(notification) : n);
          });
        }),
        catchError(
          this.messageService.handleError<EasytrackNotification>(
            this.translate.instant('LOGS.NOTIFICATIONS.TOGGLE_SEEN_FAILURE')
          )
        )
      );
  }

  readAll() {
    return this.http.put<EasytrackNotification>(`${this.serviceUrl}/all/read`, {})
      .pipe(tap(() => {
        this._notifications$.update((notifications) => {
          return notifications.map(n => (this.setNotificationAsRead(n)));
        });
      }));
  }


  public addNotification(notification: EasytrackNotification): void {
    this._notifications$.set([notification, ...this._notifications$()]);
    this.toastService.showInfo(notification.title, notification.text);
    this.playNotificationAudio();
  }

  public generateFakeNotification() {
    return this.http.post(this.serviceUrl, {
      notifType: EasytrackNotificationType.INFO,
      text: 'Cette notif a été générée à la main !',
      title: 'NOTIFICATION GENEREE',
      platform: ADMIN_APP_ID
    });
  }

  private playNotificationAudio() {
    if (this.audio.paused) {
      this.audio.play();
      setTimeout(() => {
        this.audio.pause();
        this.audio.currentTime = 0;
      }, 1000);
    }
  }

  //  ----------------------------------------------- ACCESSORS ---------------------------------------------------------------- //

  public get notifications$(): Signal<EasytrackNotification[]> {
    return this._notifications$.asReadonly();
  }

  private setNotificationAsRead(notification: EasytrackNotification) {
    return {...notification, read: true};
  };

}
